import { FC, useEffect, useMemo, useRef, useState } from 'react';

import HotTable, { HotColumn } from '@handsontable/react';
import { IList } from 'core/API/listApi';
import { CellValue } from 'handsontable/common';
import {
    PDP_INDICATOR_CRITERIA_UNITS_LIGHT,
    PDP_INDICATOR_CRITERIA_WORDS_LIGHT,
    UNITS_CHARS,
    UNITS_UNITS,
} from 'pages/ListManagement/const';
import { ICustomListData, SourceData } from 'pages/ListManagement/types';
import { arrayToKeyValue } from 'utils/arrays';
import { listAllowedEmpty, requireTitle } from './helpers/utils';
import { MPdpIndicatorTargetCheckHeader, translateData, translateHeaders } from './helpers/HeadersList';

import Grid from '../Grid';
import { listValidator, numberValidator } from '../Validators';
import { AutoCompleteRenderer, CustomIntervalRenderer } from './helpers/customRenderers';
import CustomDropboxRenderer from './helpers/CustomDropboxRenderer';
import { IRetailer } from 'shared/types';
import { useTranslation } from 'helpers/languages/translations';
import { keyBy } from 'lodash';

export const MPdpIndicatorTargetCheck: FC<{
    list: IList;
    toolId: number;
    retailers: IRetailer[];
    categories: { id: number; name: string }[];
}> = ({ list, toolId, retailers, categories }) => {
    const hotRef = useRef<HotTable | null>(null);
    const translatedHeaders = translateHeaders(MPdpIndicatorTargetCheckHeader);
    const translatedWords = translateData(PDP_INDICATOR_CRITERIA_WORDS_LIGHT);
    const translatedAttUnits = translateData(PDP_INDICATOR_CRITERIA_UNITS_LIGHT);
    const translatedUnitArray = translateData(UNITS_UNITS);
    const translatedCharArray = translateData(UNITS_CHARS);
    const [formatedList, setFormatedList] = useState<IList>(list);
    const translate = useTranslation();
    const allRetailersString = translate('list.allRetailers') as string;
    const retailersById = keyBy(retailers, 'id');
    const categoriesById = keyBy(categories, 'id');
    const retailerNames = retailers.map((ret) => ret.name).sort();
    const categoriesNames = categories.map((cat) => cat.name).sort();

    useEffect(() => {
        setFormatedList(list);
    }, [list.name]);

    const translatedUnits = [...translatedCharArray, ...translatedUnitArray];

    const mapCriteria: any = {};
    const mapIDToCriteria: any = {};
    const mapIDToUnits: any = {};
    const mapUnits: any = {};

    translatedUnits.forEach((tU, i) => {
        mapUnits[tU] = [...UNITS_CHARS, ...UNITS_UNITS][i].split('.')[2];
        mapIDToUnits[[...UNITS_CHARS, ...UNITS_UNITS][i].split('.')[2]] = tU;
    });

    translatedWords.forEach((tC, i) => {
        mapCriteria[tC] = PDP_INDICATOR_CRITERIA_WORDS_LIGHT[i].split('.')[2];
        mapIDToCriteria[PDP_INDICATOR_CRITERIA_WORDS_LIGHT[i].split('.')[2]] = tC;
    });

    translatedAttUnits.forEach((tC, i) => {
        mapCriteria[tC] = PDP_INDICATOR_CRITERIA_UNITS_LIGHT[i].split('.')[2];
        mapIDToCriteria[PDP_INDICATOR_CRITERIA_UNITS_LIGHT[i].split('.')[2]] = tC;
    });

    const sortedCriteria: ICustomListData[] = [...translatedWords, ...translatedAttUnits].map((translatedData) => {
        return {
            id: mapCriteria[translatedData] as string,
            name: translatedData,
        } as ICustomListData;
    });

    const beforeDownload = (listToDownload: CellValue[][]) => {
        const keyValueRetailers = arrayToKeyValue(sortedCriteria, 'id');
        const newData = listToDownload.map((row) => {
            const newRow = [...row];
            newRow[2] = keyValueRetailers[newRow[2] as string]?.name;
            return newRow;
        });

        return newData;
    };

    const beforeUpload = (listToUpload: SourceData) => {
        const keyValueRetailers = arrayToKeyValue(sortedCriteria, 'name');
        const uploadData = listToUpload.map((data) => {
            const newRow = [...data];
            newRow[2] = keyValueRetailers[newRow[2] as string]?.id;

            return newRow;
        });

        return uploadData;
    };

    const retailersWithAll = [allRetailersString, ...retailerNames];
    const categoriesWithEmpty = listAllowedEmpty(categoriesNames);

    const afterPaste = (data: any[], coords: any[]) => {
        const hot = hotRef?.current?.hotInstance;
        const newList = { ...formatedList };

        newList.data = data.map((row, i) => {
            const newRow = [...row];
            if (newRow[2]) {
                newRow[2] = newRow[2].replace(/\\n/g, '');
                hot?.setDataAtCell(i, 2, newRow[2]);
            }

            return newRow;
        });
        setFormatedList(newList);
    };

    const memoList = useMemo(() => {
        const listToReturn = { ...formatedList };
        listToReturn.data = formatedList.data.map((row) => {
            const formattedRow = [...row];
            if (formattedRow[0] === 0) formattedRow[0] = allRetailersString;
            else formattedRow[0] = retailersById[row[0] as number]?.name || '';

            if (formattedRow[1] === 0) formattedRow[1] = '-';
            else formattedRow[1] = categoriesById[row[1] as number]?.name || '';

            return formattedRow;
        });
        return listToReturn;
    }, [formatedList.data, formatedList.name]);

    const afterValidate = (prevList: IList) => {
        const retailersByName = keyBy(retailers, 'name');
        const categoriesByName = keyBy(categories, 'name');
        const newData = prevList.data.map((row) => {
            if (row[0] === translate('list.allRetailers')) row[0] = 0;
            else row[0] = retailersByName[row[0] as string]?.id || null;
            if (row[1] === '-') row[1] = 0;
            else row[1] = categoriesByName[row[1] as string]?.id || null;
            return row;
        });
        return {
            ...prevList,
            data: newData,
        };
    };

    return (
        <Grid
            toolId={toolId}
            headers={MPdpIndicatorTargetCheckHeader}
            list={memoList}
            paramHotRef={hotRef}
            afterPaste={afterPaste}
            beforeDownload={beforeDownload}
            beforeUpload={beforeUpload}
            afterValidate={afterValidate}
        >
            <HotColumn
                validator={listValidator(retailersWithAll)}
                title={requireTitle(translatedHeaders[0])}
                type="dropdown"
                source={retailersWithAll}
            >
                <AutoCompleteRenderer list={retailersWithAll} hot-renderer />
            </HotColumn>
            <HotColumn
                validator={listValidator(categoriesWithEmpty)}
                title={requireTitle(translatedHeaders[1])}
                type="autocomplete"
                source={categoriesWithEmpty}
            >
                <AutoCompleteRenderer list={categoriesWithEmpty} hot-renderer />
            </HotColumn>
            <HotColumn title={requireTitle(translatedHeaders[2])}>
                <CustomDropboxRenderer customList={sortedCriteria} hotInstance={hotRef} hot-renderer />
            </HotColumn>
            <HotColumn
                type="numeric"
                validator={numberValidator(false, { min: 1, max: 100 })}
                title={requireTitle(translatedHeaders[3])}
            >
                <CustomIntervalRenderer min={1} max={100} hot-renderer />
            </HotColumn>
            <HotColumn
                type="numeric"
                validator={numberValidator(false, { min: 0, max: 100 })}
                title={requireTitle(translatedHeaders[4])}
            >
                <CustomIntervalRenderer min={0} max={100} hot-renderer />
            </HotColumn>
        </Grid>
    );
};
